import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import Heading from '@arianna/design-system/Heading'
import Button from '@arianna/design-system/Button'

interface AdminLayoutProps {
  title: string
  active: 'manuals' | 'users'
  actions?: React.ReactNode
  children: React.ReactNode
}

interface AppPageProps {
  current_admin?: { id: number; email: string } | null
  flash?: { alert?: string; notice?: string }
  [key: string]: unknown
}

const NAV_ITEMS = [
  { key: 'manuals' as const, label: 'Manuali', href: '/admin/manuals' },
  { key: 'users' as const, label: 'Utenti', href: '/admin/users' },
]

function handleLogout() {
  router.delete('/admin/auth/sign_out')
}

function NavLinks({
  active,
  onNavigate,
}: {
  active: 'manuals' | 'users'
  onNavigate?: () => void
}) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV_ITEMS.map((item) => {
        const isActive = item.key === active
        return (
          <Link
            key={item.key}
            href={item.href}
            onClick={() => onNavigate?.()}
            className={
              isActive
                ? 'flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors bg-surface text-ink border-l-2 border-accent'
                : 'flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors text-ink-3 hover:text-ink hover:bg-surface/60 border-l-2 border-transparent'
            }
          >
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}

function SidebarFooter({ email }: { email: string | null | undefined }) {
  return (
    <div className="px-4 py-4 border-t border-line">
      {email && <p className="font-mono text-xs text-ink-3 truncate mb-3">{email}</p>}
      <Button type="button" variant="ghost" size="sm" onClick={handleLogout} className="w-full">
        Esci
      </Button>
    </div>
  )
}

export default function AdminLayout({ title, active, actions, children }: AdminLayoutProps) {
  const { current_admin } = usePage<AppPageProps>().props
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <div data-theme="notturno" className="min-h-[100dvh] bg-bg text-ink font-sans flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-line bg-bg-soft md:sticky md:top-0 md:h-[100dvh]">
        <div className="px-5 py-6">
          <div className="font-serif text-xl font-semibold text-ink mb-1">Arianna</div>
          <Heading variant="eyebrow">Admin</Heading>
        </div>
        <NavLinks active={active} />
        <div className="flex-1" />
        <SidebarFooter email={current_admin?.email} />
      </aside>

      {/* Drawer (mobile) */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="relative w-64 h-full bg-bg-soft border-r border-line flex flex-col">
            <div className="px-5 py-6 flex items-center justify-between">
              <div>
                <div className="font-serif text-xl font-semibold text-ink mb-1">Arianna</div>
                <Heading variant="eyebrow">Admin</Heading>
              </div>
              <button
                type="button"
                aria-label="Chiudi il menu"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center justify-center w-8 h-8 rounded-md border border-line bg-surface text-ink"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <NavLinks active={active} onNavigate={() => setDrawerOpen(false)} />
            <div className="flex-1" />
            <SidebarFooter email={current_admin?.email} />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar (mobile) */}
        <header className="md:hidden flex items-center justify-between px-4 h-14 border-b border-line bg-bg">
          <span className="font-serif text-lg font-semibold text-ink">Arianna</span>
          <button
            type="button"
            aria-label="Apri il menu"
            onClick={() => setDrawerOpen(true)}
            className="flex items-center justify-center w-9 h-9 rounded-md border border-line bg-surface text-ink"
          >
            <span aria-hidden="true">&#9776;</span>
          </button>
        </header>

        <header className="sticky top-0 z-10 border-b border-line bg-bg/95 backdrop-blur px-6 md:px-8 py-4 flex items-center justify-between gap-4">
          <div>
            <Heading variant="eyebrow">Admin</Heading>
            <Heading variant="h2">{title}</Heading>
          </div>
          {actions && <div className="flex items-center gap-3 shrink-0">{actions}</div>}
        </header>
        <main className="flex-1 px-6 md:px-8 py-8">
          <div className="mx-auto w-full max-w-5xl">{children}</div>
        </main>
      </div>
    </div>
  )
}
